import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Form, Input, Space, Tag, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import ContractCharts from "./ContractCharts";
import ContractCompanyDetailModal from "./ContractCompanyDetailModal";
import {
  CONTRACT_PRIORITIES,
  CONTRACT_STATUS_OPTIONS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  normalizeContractResponse,
} from "./constants";

const EMPTY_FILTERS = {
  title: "",
  priority: [],
  status: [],
};
const EMPTY_FACILITY_IDS = [];
const EMPTY_FACILITIES = [];
const EMPTY_COMPANY_IDS = [];
const EMPTY_COMPANIES = [];

const formatDate = (value) => {
  if (!value) return "--";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : "--";
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "--";
};

const getInitials = (name) => String(name || "?").trim().slice(0, 2).toUpperCase();

// Single-line ellipsis cell with hover tooltip showing the full content.
const EllipsisCell = ({ children, tooltip, style }) => (
  <Tooltip title={tooltip || children}>
    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%", ...style }}>
      {children}
    </div>
  </Tooltip>
);

export default function ContractList({
  facilityIds = EMPTY_FACILITY_IDS,
  facilities = EMPTY_FACILITIES,
  companyIds = EMPTY_COMPANY_IDS,
  companies = EMPTY_COMPANIES,
  filterByFacility = true,
  onFacilityChange,
  onCompanyChange,
  contractors = EMPTY_COMPANIES,
  awardedContractorIds = EMPTY_COMPANY_IDS,
  onAwardedContractorChange,
  loadRequests,
  getDetailPath,
  getWorkorderPath,
  showFacilityColumn = false,
  showCompanyColumn = false,
  showWorkorderColumn = true,
  showRequesterColumn = false,
  emptyText,
  canCreate = false,
  createPath,
  enableContractorModal = false,
  // "facility" shows the awarded (winning bid) amount; "contractor" shows the
  // current user's own submitted amount. No column is rendered otherwise.
  viewerRole,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const actionRef = useRef();
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    const queryFilters = {};
    ["priority", "status"].forEach((key) => { const values = params.getAll(key); if (values.length) queryFilters[key] = values; });
    return { ...EMPTY_FILTERS, ...(location.state || {}), ...queryFilters };
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = { ...EMPTY_FILTERS };
    ["priority", "status"].forEach((key) => {
      const values = params.getAll(key).flatMap((value) => value.split(",")).filter(Boolean);
      if (values.length) next[key] = values;
    });
    if (next.priority.length || next.status.length) setFilters((current) => ({ ...current, ...next }));
  }, [location.search]);
  const [loadedRows, setLoadedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [contractorModal, setContractorModal] = useState(null);
  const request = useCallback(async (params) => {
    if (filterByFacility && !facilityIds.length) {
      setLoadedRows([]);
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(filterByFacility ? facilityIds : undefined, {
        page: params.current,
        page_size: params.pageSize,
        company: companyIds,
        awarded_contractor: awardedContractorIds,
        title: filters.title.trim(),
        priority: filters.priority,
        status: filters.status,
      });
      const { rows, total } = normalizeContractResponse(response);
      setLoadedRows(rows);
      return { data: rows, total, success: true };
    } catch (error) {
      setLoadedRows([]);
      message.error("Failed to load contracts");
      return { data: [], total: 0, success: false };
    }
  }, [awardedContractorIds, companyIds, facilityIds, filterByFacility, filters, loadRequests]);

  // ProTable does not automatically re-run its request callback when the
  // selected facility list or filters change, so explicitly reload it.
  useEffect(() => {
    setLoadedRows([]);
    actionRef.current?.reload?.();
  }, [awardedContractorIds, companyIds, facilityIds, filters]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <EllipsisCell tooltip={value}>
          <a
            href="#contract-details"
            onClick={(event) => { event.preventDefault(); openDetail(record); }}
            style={{ color: "#1677ff", textDecoration: "underline" }}
          >
            {value}
          </a>
        </EllipsisCell>
      ),
    },
    ...(showWorkorderColumn ? [{
      title: "Work Order",
      dataIndex: "workorder_title",
      key: "workorder_title",
      width: 200,
      render: (value, record) => {
        const hasWorkorder = Boolean(record.workorder_id);
        const label =
          record.workorder_title ||
          record.workorder_number ||
          (hasWorkorder ? `Work Order #${record.workorder_id}` : "--");
        const content = hasWorkorder && getWorkorderPath ? (
          <a
            href="#workorder-details"
            onClick={(event) => {
              event.preventDefault();
              const path = getWorkorderPath(record);
              if (path) navigate(path);
            }}
            style={{ color: "#1677ff", textDecoration: "underline" }}
          >
            {label}
          </a>
        ) : label;
        return <EllipsisCell tooltip={label}>{content}</EllipsisCell>;
      },
    }] : []),
    ...(showCompanyColumn ? [{
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      width: 170,
      render: (value, record) => record.company_name || (record.company_id ? `Company #${record.company_id}` : "--"),
    }] : []),
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Awarded Contractor",
      dataIndex: "assignTo_name",
      key: "assignTo_name",
      width: 160,
      render: (_, record) => record.assignTo_name ? (
        <Space size={8}>
          <Avatar size={24} src={record.assignTo_logo}>{getInitials(record.assignTo_name)}</Avatar>
          {enableContractorModal && record.assignTo != null ? (
            <a
              href="#contractor-details"
              onClick={(event) => {
                event.preventDefault();
                setContractorModal(record);
              }}
              style={{ color: "#1677ff", textDecoration: "underline" }}
            >
              {record.assignTo_name}
            </a>
          ) : (
            <span>
              <span style={{ display: "block" }}>{record.assignTo_name}</span>
              {record.assignTo_email ? <small style={{ color: "#667085" }}>{record.assignTo_email}</small> : null}
            </span>
          )}
        </Space>
      ) : "Unclaimed",
    },
    ...(viewerRole === "facility" ? [{
      title: "Awarded Amount",
      key: "awarded_amount",
      width: 150,
      render: (_, record) => formatMoney(record.awarded_amount),
    }] : []),
    ...(viewerRole === "contractor" ? [{
      title: "Your Quote",
      key: "my_quote_amount",
      width: 130,
      render: (_, record) => formatMoney(record.my_quote_amount),
    }] : []),
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value, row) => {
        const displayStatus = row.display_status || value;
        const label = row.display_status_label || CONTRACT_STATUS_LABELS[displayStatus] || displayStatus;
        return (
          <Tag color={CONTRACT_STATUS_COLORS[displayStatus]}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Work Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 120,
      render: formatDate,
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 150,
      render: (value) => {
        if (!value) return "--";
        const date = moment(value);
        return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "--";
      },
    },
  ], [enableContractorModal, getWorkorderPath, openDetail, setContractorModal, showFacilityColumn, showCompanyColumn, showWorkorderColumn, showRequesterColumn, viewerRole]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    if (value === null || value === undefined) return;

    setFilters((current) => {
      const values = current[key] || [];
      const exists = values.some((item) => String(item) === String(value));
      return {
        ...current,
        [key]: exists
          ? values.filter((item) => String(item) !== String(value))
          : [...values, value],
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onCompanyChange?.([]);
    onFacilityChange?.([]);
    onAwardedContractorChange?.([]);
  }, [onAwardedContractorChange, onCompanyChange, onFacilityChange]);

  const renderSearches = useCallback(() => (
    <Form className="filter-form contract-filter-form" autoComplete="off">
      {companies.length > 0 ? (
        <Form.Item label="Company" name="company">
          <ResponsiveSelect
            mode="multiple"
            allowClear={Boolean(onCompanyChange)}
            showSearch
            disabled={!onCompanyChange}
            popupTitle="Select Companies"
            placeholder="Company"
            value={companyIds}
            options={companies.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onCompanyChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      {onAwardedContractorChange && contractors.length > 0 ? (
        <Form.Item label="Awarded Contractor" name="awardedContractor">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Awarded Contractors"
            placeholder="Awarded Contractor"
            value={awardedContractorIds}
            options={contractors.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onAwardedContractorChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      {filterByFacility && facilities.length > 0 ? (
        <Form.Item label="Facility" name="facility">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Facilities"
            placeholder="Facility"
            value={facilityIds}
            options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onFacilityChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Title" name="title">
        <Input
          allowClear
          placeholder="Search title"
          value={filters.title}
          onChange={(event) => updateFilter("title", event.target.value)}
        />
      </Form.Item>
      <Form.Item label="Priority" name="priority">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Priorities"
          placeholder="Priority"
          value={filters.priority}
          options={CONTRACT_PRIORITIES.map((value) => ({ value, label: value }))}
          onChange={(value) => updateFilter("priority", value || [])}
        />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Statuses"
          placeholder="Status"
          value={filters.status}
          options={CONTRACT_STATUS_OPTIONS}
          onChange={(value) => updateFilter("status", value || [])}
        />
      </Form.Item>
    </Form>
  ), [companies, companyIds, contractors, awardedContractorIds, facilities, facilityIds, filterByFacility, filters, onAwardedContractorChange, onCompanyChange, onFacilityChange, updateFilter]);

  const filterHasValue = Boolean(
    (filterByFacility && facilityIds.length) ||
    (onCompanyChange && companyIds.length) ||
    (onAwardedContractorChange && awardedContractorIds.length) ||
    filters.title.trim() ||
    filters.priority.length ||
    filters.status.length
  );

  const pageHeader = (
    <Space>
      {canCreate ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(createPath)}>
          New Contract Request
        </Button>
      ) : null}
      <PageHeader
      hide_imports
      hide_search
      hid_new
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={renderSearches}
      resetFilters={resetFilters}
      filterHasValue={filterHasValue}
      />
    </Space>
  );

  return (
    <div style={{ width: "100%", minWidth: 0, padding: 16 }}>
      <ContractCharts
        rows={loadedRows}
        filters={filters}
        onPrioritySelect={(value) => toggleFilterValue("priority", value)}
        onStatusSelect={(value) => toggleFilterValue("status", value)}
      />
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: (showFacilityColumn ? 180 : 0) + (showCompanyColumn ? 170 : 0) + (showWorkorderColumn ? 200 : 0) + (showRequesterColumn ? 160 : 0) + (viewerRole ? 150 : 0) + 1210 }}
        toolBarRender={() => pageHeader}
        locale={{ emptyText: emptyText || (filterByFacility && !facilityIds.length ? "Select one or more facilities" : "No contracts") }}
      />
      {enableContractorModal ? (
        <ContractCompanyDetailModal
          open={Boolean(contractorModal)}
          onClose={() => setContractorModal(null)}
          contractor={
            contractorModal?.assignTo != null
              ? {
                  id: contractorModal.assignTo,
                  name: contractorModal.assignTo_name,
                  email: contractorModal.assignTo_email,
                  logo: contractorModal.assignTo_logo,
                }
              : null
          }
        />
      ) : null}
    </div>
  );
}
